import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "../axios/AxiosInstance";
import { apiErrorMessage } from "../utils/apiError";
const decisions = {
  pending: "En attente",
  interview_requested: "Entretien demandé",
  accepted: "Accepté",
  declined: "Refusé",
};
const responses = {
  pending: "En attente",
  interested: "Intéressé",
  callback: "À rappeler",
  declined: "Refuse",
};
const channels = {
  phone: "Téléphone",
  email: "Email",
  sms: "SMS",
  other: "Autre",
  "client-space": "Espace client",
};
const date = (value) =>
  new Date(value).toLocaleString("fr-FR", { timeZone: "Europe/Paris" });
export default function CvDeliveryHistory({
  kind,
  id,
  refresh = 0,
  orderId,
  clientView = false,
}) {
  const [rows, setRows] = useState([]),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [loading, setLoading] = useState(true),
    [preview, setPreview] = useState(null),
    [edit, setEdit] = useState(null),
    [status, setStatus] = useState("pending"),
    [channel, setChannel] = useState("phone"),
    [comment, setComment] = useState(""),
    [busy, setBusy] = useState(false),
    [orders, setOrders] = useState([]),
    [selectedOrder, setSelectedOrder] = useState(""),
    [reviewers, setReviewers] = useState(null);
  const previous = useRef(null);
  const load = useCallback(
    async (signal) => {
      try {
        const params = {
          ...(kind && id ? { [`${kind}Id`]: id } : {}),
          ...(orderId ? { orderId } : {}),
        };
        const { data } = await axios.get("cv-delivery/presentations", {
          params,
          signal,
        });
        const signature = JSON.stringify(
          data.map((row) => [row._id, row.revision, row.status]),
        );
        if (previous.current && previous.current !== signature)
          setNotice("Le suivi a été mis à jour.");
        previous.current = signature;
        setRows(data);
        setError("");
      } catch (e) {
        if (!signal?.aborted)
          setError(
            apiErrorMessage(
              e,
              "Impossible de charger le suivi des candidatures.",
            ),
          );
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [kind, id, orderId],
  );
  useEffect(() => {
    const controller = new AbortController();
    previous.current = null;
    setLoading(true);
    load(controller.signal);
    const timer = setInterval(() => {
      if (!document.hidden) load(controller.signal);
    }, 15000);
    const focus = () => load(controller.signal);
    window.addEventListener("focus", focus);
    return () => {
      controller.abort();
      clearInterval(timer);
      window.removeEventListener("focus", focus);
    };
  }, [load, refresh]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  const view = async (row) => {
    try {
      const { data } = await axios.get(`cv-delivery/${row._id}/file`);
      setPreview(
        URL.createObjectURL(
          new Blob([Uint8Array.from(atob(data.data), (c) => c.charCodeAt(0))], {
            type: "application/pdf",
          }),
        ),
      );
    } catch (e) {
      setError(apiErrorMessage(e, "Impossible de charger le CV."));
    }
  };
  const begin = async (row, type) => {
    setError("");
    setComment("");
    setEdit({ row, type });
    setStatus(
      type === "client-decision"
        ? row.clientDecision || "pending"
        : row.candidateResponse || "pending",
    );
    setChannel("phone");
    setSelectedOrder(row.orderId || "");
    if (type === "order")
      try {
        const { data } = await axios.get(
          localStorage.getItem("userRole") === "FREELANCE"
            ? "order/freelance"
            : "order",
        );
        setOrders(
          data.filter(
            (o) => String(o.client?._id || o.client) === row.clientId,
          ),
        );
      } catch (e) {
        setError(apiErrorMessage(e, "Impossible de charger les commandes."));
      }
  };
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await axios.patch(`cv-delivery/${edit.row._id}/${edit.type}`, {
        revision: edit.row.revision || 0,
        status,
        channel,
        comment,
        orderId: selectedOrder,
      });
      setEdit(null);
      await load();
      setNotice("Retour enregistré et partagé avec les espaces concernés.");
    } catch (e) {
      setError(apiErrorMessage(e, "Impossible d’enregistrer ce retour."));
      if (e.response?.status === 409) {
        setEdit(null);
        await load();
        setError(
          "Le suivi a changé. Consultez la dernière version avant de répondre.",
        );
      }
    } finally {
      setBusy(false);
    }
  };
  const manage = async (row) => {
    try {
      const { data } = await axios.get(`cv-delivery/reviewers/${row.clientId}`);
      setReviewers({ clientId: row.clientId, rows: data });
    } catch (e) {
      setError(apiErrorMessage(e, "Impossible de charger les habilitations."));
    }
  };
  const permission = async (user, enabled) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `cv-delivery/reviewers/${reviewers.clientId}/${user._id}`,
        { enabled },
      );
      setReviewers({ ...reviewers, rows: data });
    } catch (e) {
      setError(
        apiErrorMessage(e, "Impossible de modifier cette habilitation."),
      );
    } finally {
      setBusy(false);
    }
  };
  const staff =
    !clientView &&
    ["ADMIN", "SUPERADMIN", "HYPERADMIN", "MASTERADMIN", "AGENCY"].includes(
      localStorage.getItem("userRole"),
    );
  return (
    <section className="mt-4 rounded-xl border bg-white p-4">
      <div className="flex flex-wrap justify-between gap-3 mb-3">
        <h3 className="font-semibold">
          {clientView
            ? "Candidats proposés"
            : "Suivi des présentations et envois de CV"}
        </h3>
        <button
          type="button"
          onClick={() => load()}
          className="text-blue-700 underline"
        >
          Actualiser
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-3">
        Avec ou sans commande · suivi actualisé automatiquement toutes les 15
        secondes.
      </p>
      {notice && (
        <p role="status" className="rounded bg-blue-50 p-2 mb-3">
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="text-red-700 mb-3">
          {error}
        </p>
      )}
      {loading ? (
        <p>Chargement…</p>
      ) : !rows.length && !error ? (
        <p className="text-sm text-slate-500">Aucun candidat présenté.</p>
      ) : null}
      <ul className="space-y-4">
        {rows.map((row) => (
          <li
            key={row._id}
            id={`presentation-${row._id}`}
            className="rounded-lg border p-3 text-sm"
          >
            <strong>
              {row.reference} · {row.clientName}
            </strong>
            <p>
              {row.orderId
                ? `Commande : ${row.orderId}`
                : "Présentation spontanée — sans commande"}
            </p>
            <p>Présentation : {date(row.createdAt)}</p>
            {!clientView && (
              <>
                <p>
                  Destinataire email : {row.recipientEmail} · Par{" "}
                  {row.senderName}
                </p>
                <p>
                  Email :{" "}
                  {{
                    demo: "démonstration — aucun email envoyé",
                    sent: "accepté par le service email",
                    failed: "échec",
                    sending: "confirmation en attente",
                  }[row.status] || row.status}
                </p>
              </>
            )}
            <div className="my-2 flex flex-wrap gap-2">
              <span className="rounded bg-blue-50 px-2 py-1">
                Client : {decisions[row.clientDecision] || "En attente"}
              </span>
              {!clientView && (
                <span className="rounded bg-slate-100 px-2 py-1">
                  Candidat : {responses[row.candidateResponse] || "En attente"}
                </span>
              )}
            </div>
            {clientView && row.candidateResponse === "declined" && (
              <p className="text-amber-700">
                Le candidat ne souhaite pas poursuivre cette présentation.
              </p>
            )}
            <p className="text-xs text-slate-500">
              CV · version {row.version.slice(0, 12)}
            </p>
            <div className="flex flex-wrap gap-4 mt-2">
              <button
                type="button"
                className="text-blue-700 underline"
                onClick={() => view(row)}
              >
                Voir le CV anonymisé
              </button>
              {row.canRecordCandidate && !clientView && (
                <>
                  <button
                    type="button"
                    className="text-blue-700 underline"
                    onClick={() => begin(row, "candidate-response")}
                  >
                    Enregistrer un retour candidat
                  </button>
                  <button
                    type="button"
                    className="text-blue-700 underline"
                    onClick={() => begin(row, "order")}
                  >
                    Rattacher / modifier la commande
                  </button>
                </>
              )}
              {clientView && row.canReview && (
                <button
                  type="button"
                  className="text-blue-700 underline"
                  onClick={() => begin(row, "client-decision")}
                >
                  Répondre au profil
                </button>
              )}
              {staff && (
                <button
                  type="button"
                  className="text-blue-700 underline"
                  onClick={() => manage(row)}
                >
                  Habilitations client
                </button>
              )}
            </div>
            {clientView && !row.canReview && (
              <p className="text-slate-500 mt-2">
                Consultation uniquement : votre compte n’est pas habilité à
                répondre aux profils.
              </p>
            )}
            <details className="mt-3">
              <summary>
                Historique des réponses et commentaires ({row.events.length})
              </summary>
              <ol className="mt-2 space-y-2">
                {row.events.map((event, index) => (
                  <li key={index} className="border-l pl-3">
                    <p>
                      {date(event.at)} · {event.author} ·{" "}
                      {channels[event.channel] || "Suivi"}
                    </p>
                    <p>
                      {event.type === "client-decision"
                        ? `Client : ${decisions[event.status]}`
                        : event.type === "candidate-response"
                          ? `Candidat : ${responses[event.status]}`
                          : `Commande : ${event.orderId || "aucune"}`}
                    </p>
                    {event.comment && (
                      <p className="whitespace-pre-wrap">{event.comment}</p>
                    )}
                  </li>
                ))}
              </ol>
            </details>
          </li>
        ))}
      </ul>
      {edit && (
        <form
          onSubmit={save}
          className="mt-4 rounded border-2 border-blue-200 p-4 space-y-3"
        >
          <h4 className="font-semibold">
            {edit.type === "client-decision"
              ? "Réponse du client"
              : edit.type === "order"
                ? "Commande associée"
                : "Retour du candidat"}{" "}
            · {edit.row.reference}
          </h4>
          {edit.type === "order" ? (
            <label className="block">
              Commande
              <select
                aria-label="Commande associée"
                className="block border rounded p-2 w-full"
                value={selectedOrder}
                onChange={(e) => setSelectedOrder(e.target.value)}
              >
                <option value="">Sans commande</option>
                {orders.map((o) => (
                  <option key={o._id} value={o._id}>
                    {o.orderNumber || o._id} · {o.qualification || ""}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <>
              <label className="block">
                Réponse
                <select
                  aria-label="Réponse"
                  className="block border rounded p-2 w-full"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {Object.entries(
                    edit.type === "client-decision" ? decisions : responses,
                  ).map(([value, text]) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ))}
                </select>
              </label>
              {edit.type === "candidate-response" && (
                <label className="block">
                  Canal
                  <select
                    aria-label="Canal"
                    className="block border rounded p-2 w-full"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                  >
                    {Object.entries(channels)
                      .filter(([key]) => key !== "client-space")
                      .map(([value, text]) => (
                        <option key={value} value={value}>
                          {text}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              <label className="block">
                {status === "declined" ? "Motif du refus" : "Commentaire"}
                <textarea
                  aria-label="Commentaire du retour"
                  className="block border rounded p-2 w-full"
                  required={
                    edit.type === "candidate-response" || status === "declined"
                  }
                  maxLength={2000}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </label>
              {edit.type === "candidate-response" && (
                <p className="text-xs text-slate-500">
                  Ce commentaire reste interne à l’agence et au freelance.
                </p>
              )}
            </>
          )}
          <button
            disabled={busy}
            type="submit"
            className="rounded bg-blue-700 px-4 py-2 text-white"
          >
            Enregistrer le retour
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setEdit(null)}
            className="ml-4"
          >
            Annuler
          </button>
        </form>
      )}
      {reviewers && (
        <section className="mt-4 border rounded p-4">
          <h4 className="font-semibold">
            Comptes autorisés à répondre aux candidats
          </h4>
          {!reviewers.rows.length && (
            <p>Aucun compte client actif rattaché à cette entreprise.</p>
          )}
          {reviewers.rows.map((user) => (
            <label className="block my-2" key={user._id}>
              <input
                type="checkbox"
                checked={user.canReview}
                disabled={busy}
                onChange={(e) => permission(user, e.target.checked)}
              />{" "}
              {user.name} · {user.email}
            </label>
          ))}
          <button
            type="button"
            onClick={() => setReviewers(null)}
            className="underline"
          >
            Fermer
          </button>
        </section>
      )}
      {preview && (
        <div className="mt-3">
          <button
            type="button"
            className="text-blue-700 mb-2"
            onClick={() => setPreview(null)}
          >
            Fermer l’aperçu
          </button>
          <iframe
            title="Version du CV envoyé"
            src={preview}
            className="w-full h-[60vh] border"
          />
        </div>
      )}
    </section>
  );
}
